import { formatDate } from "@memorang/helpers/date";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { FlatCard } from "../Cards/FlatCard";
import { ScrollButtonFlatlist } from "../ChatUI/ScrollButtonFlatlist";
import CitationCarousel from "../CitationCard/CitationCarousel";
import { CustomAppBar } from "../CustomAppbar";
import Image from "../CustomImage";
import { ExpandableText } from "../ExpandableText";
import { PodcastPlayer } from "../PodcastPlayer";
import ScrollContainer from "../ScrollContainer";
import ActionRow from "./ActionRow";
import type { PodcastDetailAppletProps } from "./types";

export type { PodcastDetailAppletProps };
export type {
	NowPlayingProps,
	PodcastCitation,
	PodcastEpisode,
} from "./types";

export const PodcastDetailApplet = ({
	collectionTitle,
	episode,
	isBookmarked,
	isInQueue,
	onBackPress,
	onToggleBookmark,
	onToggleQueue,
	onPlayPress,
	onPressCitation,
	nowPlayingProps,
}: PodcastDetailAppletProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const [showNowPlaying, setShowNowPlaying] = useState(false);
	const showDesktopPlayer = showNowPlaying || !!nowPlayingProps?.isPlaying;

	const handlePlayPress = () => {
		if (nowPlayingProps) {
			setShowNowPlaying(true);
			if (nowPlayingProps.isPlaying) {
				return;
			}
		}
		onPlayPress?.();
	};

	const body = (
		<Box gap={isMobile ? 16 : 24} width="100%">
			<Box gap={isMobile ? 16 : 24} alignItems="center" width="100%">
				<Image
					source={{ uri: episode.artworkUri }}
					accessibilityLabel={t("podcast.a11y.artwork", {
						title: episode.title,
					})}
					style={{
						width: 220,
						height: 220,
						borderRadius: theme.roundness * 3,
					}}
					contentFit="cover"
				/>
				<Box gap={12} width="100%" alignItems="center">
					<Text
						variant={isMobile ? "titleMedium" : "headlineSmall"}
						style={{ color: theme.colors.onSurface, textAlign: "center" }}
					>
						{episode.title}
					</Text>
					{!!episode.author && (
						<Text
							variant="labelMedium"
							style={{
								color: theme.colors.onSurfaceVariant,
								textAlign: "center",
							}}
						>
							{episode.author}
						</Text>
					)}
					<Text
						variant="bodyMedium"
						style={{
							color: theme.colors.onSurfaceVariant,
							textAlign: "center",
						}}
					>
						{formatDate(episode.date, "en-GB", {
							day: "numeric",
							month: "short",
							year: "numeric",
							timeZone: "UTC",
						})}
					</Text>
					<ActionRow
						durationSeconds={episode.durationSeconds}
						isPlaying={!!nowPlayingProps?.isPlaying}
						isInQueue={isInQueue}
						onPlayPress={handlePlayPress}
						onToggleQueue={onToggleQueue}
					/>
				</Box>
			</Box>

			<Divider />

			<Box gap={32} alignItems="flex-start" width="100%">
				<Box gap={4} width="100%">
					<Text variant="titleMedium" style={{ color: theme.colors.onSurface }}>
						{t("podcast.detail.descriptionHeading")}
					</Text>
					<ExpandableText
						text={episode.description}
						color={theme.colors.onSurfaceVariant}
						expandLinkColor={theme.colors.primary}
						resetKey={episode.id}
					/>
				</Box>

				{episode.tags.length > 0 && (
					<Box gap={8} width="100%">
						<Text
							variant="labelMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{t("podcast.detail.tagsHeading")}
						</Text>
						<ScrollButtonFlatlist
							data={episode.tags}
							horizontal
							showsHorizontalScrollIndicator={false}
							keyExtractor={(tag) => tag}
							contentContainerStyle={{ gap: 8 }}
							renderItem={({ item: tag }) => (
								<Box
									height={32}
									paddingHorizontal={16}
									justifyContent="center"
									borderRadius={theme.roundness * 100}
									backgroundColor={theme.colors.surfaceVariant}
								>
									<Text
										variant="labelLarge"
										style={{ color: theme.colors.primary }}
									>
										{tag}
									</Text>
								</Box>
							)}
						/>
					</Box>
				)}

				{!!episode.citations?.length && (
					<Box gap={8} width="100%">
						<Text
							variant="labelMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{t("podcast.detail.citationsHeading")}
						</Text>
						<CitationCarousel
							citations={episode.citations}
							onPressCitation={onPressCitation}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);

	const playerProps = nowPlayingProps && {
		...nowPlayingProps,
		isBookmarked,
		onToggleBookmark,
		collectionTitle,
		onBackPress: () => setShowNowPlaying(false),
	};

	return (
		<Box flex={1} backgroundColor={theme.colors.background}>
			<CustomAppBar title={collectionTitle} onPressBack={onBackPress} />

			<ScrollContainer maxWidth="md">
				{isMobile ? (
					<Box paddingTop={24} paddingHorizontal={16} paddingBottom={24}>
						{body}
					</Box>
				) : (
					<Box
						paddingTop={32}
						paddingBottom={24}
						paddingHorizontal={16}
						alignItems="center"
						width="100%"
					>
						<FlatCard
							style={{
								borderRadius: theme.roundness * 3,
								width: "100%",
								maxWidth: 840,
							}}
						>
							<Box padding={24}>{body}</Box>
						</FlatCard>
						{showDesktopPlayer && <Box height={96} />}
					</Box>
				)}
			</ScrollContainer>

			{playerProps &&
				(isMobile && showNowPlaying ? (
					<Box
						position="absolute"
						top={0}
						left={0}
						right={0}
						bottom={0}
						zIndex={1000}
					>
						<PodcastPlayer {...playerProps} visible />
					</Box>
				) : (
					<PodcastPlayer
						{...playerProps}
						visible={isMobile ? showNowPlaying : showDesktopPlayer}
					/>
				))}
		</Box>
	);
};
